import React, { useState } from "react";
import Loader from "../../components/Loader";

const hiddenProductAttributeKeys = new Set([
  "Spider_Source",
  "Spider_Source_Product_Code",
  "Spider_Source_Product_Id",
  "Spider_Source_Url",
  "Spider_Source_Image_Url",
  "Spider_Source_Category_Name",
  "Spider_Source_Category_Path",
  "TODO_Remark",
]);

/**
 * 获取前台可展示的属性筛选名称。
 * @param {Record<string, unknown>} attributesValues 属性和值聚合数据
 * @returns {string[]} 可展示的属性名称列表（Manufacturer 始终排在最左侧）
 */
const getVisibleAttributeNames = (attributesValues = {}) => {
  const names = Object.keys(attributesValues).filter(
    (attributeName) =>
      !attributeName.startsWith("Spider_") &&
      !hiddenProductAttributeKeys.has(attributeName)
  );

  // 确保 Manufacturer 在最左侧
  if (names.includes("Manufacturer")) {
    const otherNames = names.filter((name) => name !== "Manufacturer");
    return ["Manufacturer", ...otherNames];
  }

  return names;
};

/**
 * 根据列内搜索关键字过滤属性值，并按“数字在前，A-Z在后”的逻辑排序。
 * @param {unknown[]} values 当前属性的全部可选值
 * @param {string} keyword 搜索关键字
 * @returns {string[]} 过滤后的排序属性值列表
 */
const getFilteredAttributeValues = (values = [], keyword = "") => {
  const normalizedKeyword = keyword.trim().toLowerCase();

  return values
    .map((value) => `${value}`)
    .filter((value) => {
      if (!normalizedKeyword) return true;
      return value.toLowerCase().includes(normalizedKeyword);
    })
    .sort((a, b) => {
      // 使用 localeCompare 的 numeric: true 选项实现自然排序（数字在前，且按数值大小排）
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
};

/**
 * 在分类介绍下方横向展示商品属性筛选项。
 * @param {Object} props 组件属性
 * @param {Object} props.parentAttributes 属性和值聚合数据
 * @param {boolean} props.isLoadingValues 是否正在加载属性值
 * @param {Function} props.setOptions 更新当前筛选条件
 * @param {Object} props.selectedOptions 当前已选择的筛选条件
 * @returns {JSX.Element|null} 横向属性筛选区域
 */
const InlineAttributesFilter = ({
  parentAttributes,
  isLoadingValues,
  setOptions,
  selectedOptions = {},
}) => {
  const [filterKeywords, setFilterKeywords] = useState({});
  const attributesValues = parentAttributes?.attributesValues || {};
  const attributeNames = getVisibleAttributeNames(attributesValues);

  /**
   * 根据用户点击更新属性筛选条件（支持多选）。
   * @param {string} attributeName 属性名称
   * @param {string} value 属性值
   * @returns {void}
   */
  const handleAttributeChange = (attributeName, value) => {
    setOptions((prev) => {
      const options = { ...prev };
      const currentValues = Array.isArray(options[attributeName])
        ? options[attributeName]
        : options[attributeName]
        ? [options[attributeName]]
        : [];

      if (currentValues.includes(value)) {
        // 如果已选中，则移除
        const newValues = currentValues.filter((v) => v !== value);
        if (newValues.length > 0) {
          options[attributeName] = newValues;
        } else {
          delete options[attributeName];
        }
      } else {
        // 如果未选中，则添加
        options[attributeName] = [...currentValues, value];
      }

      return options;
    });
  };

  /**
   * 更新单个属性列的搜索关键字。
   * @param {React.ChangeEvent<HTMLInputElement>} event 输入框变更事件
   * @returns {void}
   */
  const handleFilterKeywordChange = (event) => {
    const { name, value } = event.target;

    setFilterKeywords((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * 清空所有属性筛选条件。
   * @returns {void}
   */
  const handleClearFilters = () => {
    setOptions({});
  };

  if (isLoadingValues) return <Loader />;
  if (attributeNames.length <= 0) return null;

  return (
    <section className="w-full mb-6 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 py-3">
        <h2 className="text-xl font-bold text-gray-900">Filters</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-700">Filter Options:</span>
          <button
            type="button"
            className="border border-gray-300 bg-gray-50 px-3 py-2 text-gray-600"
          >
            Stacked
          </button>
          <button
            type="button"
            className="border border-[#1f6feb] px-3 py-2 font-semibold text-[#1f6feb]"
          >
            Scrolling
          </button>
        </div>
        <button
          type="button"
          onClick={handleClearFilters}
          className="border border-[#1f6feb] px-4 py-2 text-sm font-semibold text-[#1f6feb] hover:bg-[#f0f6ff]"
        >
          Clear Filters
        </button>
      </div>
      <div className="w-full overflow-x-auto">
        <div className="flex min-w-max border border-gray-200 bg-[#f7f7f7]">
          {attributeNames.map((attributeName) => {
            const filteredValues = getFilteredAttributeValues(
              attributesValues[attributeName],
              filterKeywords[attributeName] || ""
            );

            return (
              <section
                key={attributeName}
                className="flex h-[220px] w-[220px] shrink-0 flex-col border-r border-gray-200 bg-white last:border-r-0"
              >
                <h3 className="line-clamp-2 min-h-[42px] px-4 py-2 text-xs font-bold text-gray-900">
                  {attributeName}
                </h3>
                <div className="px-4 pb-2">
                  <input
                    type="text"
                    name={attributeName}
                    value={filterKeywords[attributeName] || ""}
                    onChange={handleFilterKeywordChange}
                    placeholder="Search Filter"
                    className="h-8 w-full border border-gray-300 px-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-[#1f6feb] focus:outline-none"
                  />
                </div>
                <div className="flex-1 overflow-y-auto px-4 pb-3">
                  {filteredValues.length > 0 ? (
                    <ul className="space-y-1">
                      {filteredValues.map((item, index) => {
                        const isSelected = Array.isArray(selectedOptions[attributeName])
                          ? selectedOptions[attributeName].includes(item)
                          : selectedOptions[attributeName] === item;

                        return (
                          <li key={`${attributeName}-${item}-${index}`}>
                            <button
                              type="button"
                              onClick={() =>
                                handleAttributeChange(attributeName, item)
                              }
                              className={`w-full truncate px-1 py-0.5 text-left text-sm hover:bg-[#f0f6ff] ${
                                isSelected
                                  ? "font-semibold text-[#1f6feb]"
                                  : "text-gray-900"
                              }`}
                              title={item}
                            >
                              {item}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400">No options</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InlineAttributesFilter;
